/**
 * Submit Claim Page - 4-step wizard
 *
 * Step 0: Select claim type (Installed, Uninstalled, Job Stop, Workmanship, BFS, SunSuite, Other)
 * Step 1: Select a registration (holding)
 * Step 2: Claim details form (fields from creation metadata)
 * Step 3: Review + submit
 */

import {
  useReducer,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  Hammer,
  Package,
  OctagonAlert,
  Wrench,
  MoreHorizontal,
  Layers,
  Sun,
  Building2,
  CircleDashed,
  Plus,
  Search,
  MapPin,
  ChevronLeft,
  Home,
  type LucideIcon,
} from 'lucide-react'
import { SearchInput } from '@/shared/components/ui/search-input'
import { AppShell } from '@/admin/components/app-shell'
import { ConfigHelp, EditConfigLink, type ConfigSectionKey } from '@/admin/components/config-help'
import { BulkImportDialog } from '@/admin/components/imports/bulk-import-dialog'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { useHoldings, useHolding, useUploadFile } from '@/admin/hooks/use-products'
import { useHostSearch, type HostSearchResult } from '@/admin/hooks/use-host-search'
import {
  useRegistrationSettings,
  hostNounForDetailView,
} from '@/admin/hooks/use-registration-settings'
import {
  useCreateClaimMetadata,
  useCreateClaim,
  useCheckClaimDuplicates,
  useClaimTypeOptions,
  useMultiItemClaimTypes,
  useClaimDisclaimers,
  useMatchingRegistration,
  type ActionFieldMeta,
  type ClaimTypeOption,
  type MatchingRegistration,
  type PotentialDuplicateClaim,
} from '@/admin/hooks/use-claims'
import { DisclaimerAck } from '@/shared/components/claims/disclaimer-ack'
import {
  disclaimerAckValue,
  disclaimerBlocks,
} from '@/shared/components/claims/disclaimer'
import { useEntityAttribute } from '@/admin/hooks/use-entity-registration'
import {
  useWizardCacheKey,
  useWizardCachePersistence,
  readWizardCache,
  clearWizardCache,
} from '@/admin/hooks/use-wizard-cache'
import { Button } from '@/shared/components/ui/button'
import { RegistrationMatchPanel } from '@/admin/components/claims/registration-match-panel'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { cn } from '@/shared/lib/utils'
import {
  MetadataField,
  normaliseClaimField,
  formatReviewValue,
  FILE_FIELD_TYPES,
  resolveDependentOptions,
  isFieldVisible,
  isConditionallyRequired,
  isRequiredValueProvided,
} from '@/shared/lib/forms'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { formatRegistrationReference } from '@/admin/lib/formatting'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import { AddressLookupProvider } from '@/shared/components/ui/address-lookup-context'
import { createAddressLookupApi } from '@/admin/hooks/use-lookups'
import { PotentialDuplicateClaimDialog } from '@/admin/components/claims/potential-duplicate-claim-dialog'

// ── State ────────────────────────────────────────────────

import { toastApiError, extractApiErrorMessage } from '@/shared/lib/api-error'

// Claim type taxonomy is sourced from the ``claim_type`` ClaimAttribute
// SELECT options served by /api/claims/claim-type-options/. Per-option
// ``icon``, ``description``, and ``active`` keys in the option payload
// carry the data the wizard used to hardcode. Phase 7 of the
// tenant-agnostic claims migration.
type ClaimType = string

// Whitelist of Lucide icons the wizard ships. Backend stores icon
// names as strings; the registry maps them to the React components.
// Add new names as tenants need them - central allowlist keeps the
// bundle from importing every icon in the library.
const CLAIM_TYPE_ICON_REGISTRY: Record<string, LucideIcon> = {
  Hammer,
  Package,
  OctagonAlert,
  Wrench,
  MoreHorizontal,
  // BFS (Basement Finishing System) and SunSuite tiles - seeded by
  // seed_oc_bfs_sunsuite_claims_workflow.
  Layers,
  Sun,
  Building2,
}

function resolveClaimTypeIcon(name?: string): LucideIcon {
  if (!name) return MoreHorizontal
  return CLAIM_TYPE_ICON_REGISTRY[name] ?? MoreHorizontal
}

// Step keys are stable identifiers; the visible step *list* is derived
// from entity-attribute registration so a tenant whose claim schema
// does not declare a ``claim_type`` attribute skips the wizard's Step
// 0 entirely. Per the Build-Time Decision Framework in CLAUDE.md -
// every conditional UI surface flows through entity presence, never
// through tenant-slug checks or hardcoded constants.
const ALL_STEP_KEYS = [
  'claim_type',
  'holding',
  'details',
  'review',
] as const
type StepKey = (typeof ALL_STEP_KEYS)[number]

const CLAIM_RETURN_LABEL = 'Submit claim'

function configSectionsForClaimStep(step: StepKey | undefined): ConfigSectionKey[] {
  switch (step) {
    case 'claim_type':
      return ['claim.type']
    case 'holding':
      return ['claim.holding_lookup']
    case 'details':
      return ['claim.details', 'claim.attachments']
    case 'review':
      return ['claim.type', 'claim.details']
    default:
      return ['claim.details']
  }
}

function primaryConfigSectionForClaimStep(
  step: StepKey | undefined,
): ConfigSectionKey | null {
  switch (step) {
    case 'claim_type':
      return 'claim.type'
    case 'holding':
      return 'claim.holding_lookup'
    case 'details':
      return 'claim.details'
    default:
      return null
  }
}

const STEP_LABELS: Record<StepKey, string> = {
  claim_type: 'Claim Type',
  holding: 'Select Registration',
  details: 'Claim Details',
  review: 'Review & Submit',
}

interface WizardState {
  // ``step`` is the StepKey index *into the visible step list*, not
  // into ``ALL_STEP_KEYS``. The reducer + handlers translate via the
  // visible-step array kept in component scope.
  step: number
  claimType: ClaimType | null
  /** Whether the selected claim type requires a holding (registration). */
  requiresHolding: boolean
  /**
   * Whether the selected claim type provisions its own registration on submit
   * (e.g. BFS). Drives the "How to start" step where the user chooses between
   * starting the claim directly or registering the structure first.
   */
  createsRegistration: boolean
  holdingUuid: string | null
  holdingDisplay: string | null
  /**
   * The selected holding's registration number, surfaced on the Review &
   * Submit step alongside the product display so the "Registration" row is
   * not just a product name (WPS-165 1-1).
   */
  holdingRegistrationNumber: string | null
  /**
   * The selected holding's *own* product template name (distinct from
   * ``productTemplateName`` above, which is the claim-type-associated
   * template used to filter the registration wizard). Drives the
   * ``product_impacted`` option filter on the create-claim metadata and,
   * crucially, lets that query cache per (schema, template) instead of per
   * holding - two holdings on the same template share one cache entry.
   */
  holdingProductTemplateName: string | null
  /**
   * WPS-119: the existing registration this property was matched to, for claim
   * types that provision their own (BFS / SunSuite). Null until the owner and
   * address are entered; `{matched: false}` once checked with no match.
   */
  matchedRegistration: MatchingRegistration | null
  /**
   * Whether to file against `matchedRegistration` rather than create a second
   * one. Attaching simply sets `holdingUuid`, which is the switch the backend
   * already reads: present -> attach to that holding, null -> provision a new
   * registration on submit. So no change to the submit payload is needed.
   */
  attachToMatchedRegistration: boolean
  /**
   * The match lookup ran and failed. Distinct from `matchedRegistration:
   * null` (not enough typed in yet) because it changes what we can honestly
   * promise on the review step: we know a registration will be created, but
   * not that there was no existing one to attach to.
   */
  matchCheckFailed: boolean
  capturedValues: Record<string, unknown>
  /**
   * When set, the "New Registration" button passes this to the registration
   * wizard so it filters its product list to only the named product template
   * (e.g. "Basement Finishing System"). Null means show all products.
   */
  productTemplateName: string | null
}

const initialState: WizardState = {
  step: 0,
  claimType: null,
  requiresHolding: true,
  createsRegistration: false,
  holdingUuid: null,
  holdingDisplay: null,
  holdingRegistrationNumber: null,
  holdingProductTemplateName: null,
  matchedRegistration: null,
  attachToMatchedRegistration: false,
  matchCheckFailed: false,
  capturedValues: {},
  productTemplateName: null,
}

type Action =
  | { type: 'UPDATE'; payload: Partial<WizardState> }
  | { type: 'NEXT'; maxStep: number }
  | { type: 'BACK'; firstKey: StepKey }
  | { type: 'GO_TO'; step: number }

function reducer(state: WizardState, action: Action): WizardState {
  switch (action.type) {
    case 'UPDATE':
      return {
        ...state,
        ...action.payload,
        // Merge capturedValues rather than replace, so a partial update never
        // silently drops already-captured answers. This matters when resuming
        // from cache at step 0: picking a claim type sends
        // `capturedValues: { claim_type }`, which a shallow merge would use to
        // wipe every restored field value.
        ...(action.payload.capturedValues
          ? {
              capturedValues: {
                ...state.capturedValues,
                ...action.payload.capturedValues,
              },
            }
          : {}),
      }
    case 'NEXT':
      return { ...state, step: Math.min(state.step + 1, action.maxStep) }
    case 'BACK': {
      const prevStep = Math.max(state.step - 1, 0)
      // When stepping back to the first visible step *if* that step is
      // the claim-type picker, clear the picked value so re-entry is
      // a clean choice. For tenants without a claim-type step the
      // first visible step is the registration picker; nothing to
      // clear.
      return {
        ...state,
        step: prevStep,
        ...(prevStep === 0 && action.firstKey === 'claim_type'
          ? {
              claimType: null,
              requiresHolding: true,
              createsRegistration: false,
              matchedRegistration: null,
              attachToMatchedRegistration: false,
              matchCheckFailed: false,
            }
          : {}),
      }
    }
    case 'GO_TO':
      return { ...state, step: action.step }
    default:
      return state
  }
}

/**
 * Name the registration's product for the claim-intake surfaces.
 *
 * A product is two levels: a line (the parent) and the specific item that was
 * registered (the child), and the child is named for whatever distinguishes it
 * from its siblings - usually a colour. Rendering both gives
 * "Duration Premium - Driftwood".
 *
 * `showVariant` decides whether the item is named at all. Tenants whose items
 * differ by colour read it as noise at intake, where the job is to identify the
 * registration rather than describe the defect; tenants whose items differ by
 * size or grade need it. Driven by ``show_product_variant_at_claim_intake`` so
 * this stays a tenant setting rather than a rule about anyone's catalogue.
 *
 * The item name is still the fallback when there is no parent, so a product
 * that was registered at the top level never renders blank.
 */
function buildProductDisplay(
  product?: HoldingRow['product'],
  showVariant = true,
): string {
  if (!product) return 'Unknown Product'
  const variant = product.cached_short_name || ''
  const parentName = product.parent_product?.cached_short_name
  const templateName = product.product_template?.name
  const categoryName = product.category?.name
  const parts: string[] = []
  if (parentName) parts.push(parentName)
  if (showVariant && variant && variant !== parentName) parts.push(variant)
  const productLine = parts.join(' - ') || variant || 'Unknown Product'
  const meta = [templateName, categoryName].filter(Boolean).join(' / ')
  return meta ? `${productLine} (${meta})` : productLine
}

// ── Component ────────────────────────────────────────────

export function SubmitClaimPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Providers for ENTITY_SEARCH claim fields (e.g. the retailer picker that
  // pre-fills the retailer contact rows). Mounted at the page root so any
  // step's picker resolves its provider by key.
  const resourceLookupProviders = useResourceLookupProviders()
  // Address lookup for ADDRESS claim fields (e.g. uninstalled Material Location,
  // WP1-106) so the shared AddressInput's search/autocomplete resolves.
  const queryClient = useQueryClient()
  const addressLookupApi = useMemo(
    () => createAddressLookupApi(queryClient),
    [queryClient],
  )
  // Whether the intake surfaces name the specific registered item next to its
  // product line. Per-tenant, because whether that name carries information
  // depends on what distinguishes one item from another in their catalogue.
  const { data: pageRegistrationSettings } = useRegistrationSettings()
  const showProductVariant =
    pageRegistrationSettings?.show_product_variant_at_claim_intake ?? true
  const bulkImportsEnabled = useFeatureFlag(FLAGS.BULK_IMPORTS)
  const multiItemEnabled = useFeatureFlag(FLAGS.MULTI_ITEM_CLAIMS)
  // The single-vs-multi fork is driven by the multi_item_claims capability
  // (sourced from config), NOT by claim_type options - so it appears even for
  // tenants that don't model a claim_type attribute.
  const { data: multiItemData } = useMultiItemClaimTypes(multiItemEnabled)
  const multiItemTypes = useMemo(
    () => multiItemData?.types ?? [],
    [multiItemData],
  )
  const [forkResolved, setForkResolved] = useState(false)
  const [bulkImportOpen, setBulkImportOpen] = useState(false)
  const [bulkImportType, setBulkImportType] = useState<string | undefined>(undefined)
  const preselectedHoldingUuid = searchParams.get('holding')
  const urlClaimSchemaKey = searchParams.get('claimSchemaKey')
  // Claim type carried through a registration round-trip (e.g. an HOA
  // claim that routed the user to register the community first). Lets us
  // restore the right claim type instead of defaulting to 'installed'.
  const preselectedClaimType = searchParams.get('claim_type')
  const tourStep = searchParams.get('tourStep')
  // Resume an in-progress claim from cache: restore BOTH the captured values
  // AND the step the user was on. The cache only survives a hard page reload
  // (see `useWizardCachePersistence` - it is cleared on in-app navigation), so
  // restoring the step lands the user back exactly where the reload
  // interrupted them. Skipped when the URL preselects a holding/schema (an
  // explicit entry point overrides a stale cached draft).
  const cacheKey = useWizardCacheKey('submit-claim')
  const hasUrlEntryPoint = Boolean(
    preselectedHoldingUuid || urlClaimSchemaKey || tourStep,
  )
  const { t } = useTranslation()
  // Claim types, so the header can name the one picked.
  const { data: claimTypeData } = useClaimTypeOptions()
  const [state, dispatch] = useReducer(
    reducer,
    initialState,
    (fallback): WizardState => {
      if (hasUrlEntryPoint) return fallback
      const cached = readWizardCache<WizardState>(cacheKey)
      // The step is clamped at render time (`currentStepKey` falls back to the
      // first visible step) so a step that is now out of range after a config
      // change still renders safely.
      //
      // Merge over `fallback` (the freshly-built initial state, not a bare
      // `{ ...cached }`) so a draft written by an older build that predates a
      // newly-added state field still gets a sane default for it instead of
      // `undefined`; the cached values (and step) win via the trailing spread.
      return cached ? { ...fallback, ...cached } : fallback
    },
  )
  const [submitted, setSubmitted] = useState(false)
  useWizardCachePersistence(cacheKey, state, submitted)

  // The ``claim_type`` attribute is configured per-tenant on the
  // claim entity. When it is not registered for the active tenant
  // the wizard skips Step 0 entirely - no hardcoded "is OC" check;
  // pure entity-presence routing.
  const hasClaimType = useEntityAttribute('claim', 'claim_type')

  // Holdingless claim types (uninstalled, job_stop) skip the holding
  // step - they go straight from type selection to details. The
  // ``requires_holding`` flag comes from the backend type option data
  // (via ``ClaimTypeOption.requires_holding``) and is set in the
  // wizard state when the user picks a type.
  const visibleStepKeys = useMemo<StepKey[]>(
    () =>
      ALL_STEP_KEYS.filter((k) => {
        if (k === 'claim_type' && !hasClaimType) return false
        if (k === 'holding' && !state.requiresHolding) return false
        return true
      }),
    [hasClaimType, state.requiresHolding],
  )
  const currentStepKey = visibleStepKeys[state.step] ?? visibleStepKeys[0]
  // The displayed position, clamped into the visible range so the stepper pips
  // stay consistent with `currentStepKey` when a cached step is out of range
  // after a config change (e.g. the holding step became hidden).
  const currentStepIndex = Math.min(
    state.step,
    Math.max(visibleStepKeys.length - 1, 0),
  )
  const visibleStepLabels = useMemo(
    () => visibleStepKeys.map((k) => STEP_LABELS[k]),
    [visibleStepKeys],
  )

  const update = useCallback(
    (payload: Partial<WizardState>) => dispatch({ type: 'UPDATE', payload }),
    [],
  )
  const next = useCallback(
    () => dispatch({ type: 'NEXT', maxStep: visibleStepKeys.length - 1 }),
    [visibleStepKeys.length],
  )
  const back = useCallback(
    () => dispatch({ type: 'BACK', firstKey: visibleStepKeys[0] }),
    [visibleStepKeys],
  )

  // When arriving from a registration detail page, pre-select the holding
  const { data: preselectedHolding } = useHolding(
    preselectedHoldingUuid && !state.holdingUuid ? preselectedHoldingUuid : null,
  )

  // Tour-driven wizard advancement - fetch first holding for details step
  const tourFirstHoldingParams = useMemo(
    () => (tourStep === 'details' ? { page: '1', page_size: '1' } : undefined),
    [tourStep],
  )
  const { data: tourHoldingsData } = useHoldings(tourFirstHoldingParams)

  useEffect(() => {
    if (tourStep === 'holding' && !preselectedHoldingUuid) {
      const holdingIdx = visibleStepKeys.indexOf('holding')
      if (holdingIdx >= 0 && state.step !== holdingIdx) {
        dispatch({
          type: 'UPDATE',
          payload: {
            claimType: hasClaimType ? (urlClaimSchemaKey || 'installed') : null,
            step: holdingIdx,
          },
        })
      }
    }
  }, [tourStep, preselectedHoldingUuid, visibleStepKeys, hasClaimType, state.step, urlClaimSchemaKey])

  useEffect(() => {
    if (tourStep === 'details' && tourHoldingsData?.results?.[0] && !state.holdingUuid) {
      const first = tourHoldingsData.results[0] as HoldingRow
      const detailsIdx = visibleStepKeys.indexOf('details')
      if (detailsIdx >= 0) {
        dispatch({
          type: 'UPDATE',
          payload: {
            claimType: hasClaimType ? (urlClaimSchemaKey || 'installed') : null,
            holdingUuid: first.uuid,
            holdingDisplay: buildProductDisplay(first.product, showProductVariant),
            holdingRegistrationNumber: first.registration_number ?? null,
            holdingProductTemplateName:
              first.product?.product_template?.name ?? null,
            step: detailsIdx,
          },
        })
      }
    }
  }, [tourStep, tourHoldingsData, state.holdingUuid, visibleStepKeys, hasClaimType, urlClaimSchemaKey, showProductVariant])

  useEffect(() => {
    if (preselectedHolding && !state.holdingUuid) {
      // Jump straight to the "details" step. Compute the index in
      // ``visibleStepKeys`` rather than hardcoding ``2`` - tenants
      // without a claim-type step have ``details`` at index 1.
      const detailsIdx = visibleStepKeys.indexOf('details')
      // Restore the claim type carried through the registration round-trip
      // so the right type/schema is preselected. The registration wizard
      // threads it back either as ?claimSchemaKey=bfs (schema-keyed flows)
      // or ?claim_type=hoa (HOA round-trip). Falls back to 'installed' for
      // the standard "New Registration" flow.
      const resolvedType = hasClaimType
        ? urlClaimSchemaKey || preselectedClaimType || 'installed'
        : null
      dispatch({
        type: 'UPDATE',
        payload: {
          holdingUuid: preselectedHolding.uuid,
          holdingDisplay: buildProductDisplay(
            preselectedHolding.product as HoldingRow['product'],
            showProductVariant,
          ),
          holdingRegistrationNumber:
            (preselectedHolding as unknown as HoldingRow).registration_number ??
            null,
          holdingProductTemplateName:
            (preselectedHolding.product as HoldingRow['product'])
              ?.product_template?.name ?? null,
          claimType: resolvedType,
          requiresHolding: true,
          capturedValues: resolvedType ? { claim_type: resolvedType } : {},
          step: detailsIdx >= 0 ? detailsIdx : 1,
        },
      })
    }
  }, [
    preselectedHolding,
    state.holdingUuid,
    visibleStepKeys,
    hasClaimType,
    urlClaimSchemaKey,
    preselectedClaimType,
    showProductVariant,
  ])

  // Fetch creation metadata. Always pass ``claimType`` as the
  // ``claim_schema_key`` so the backend resolves the CORRECT workflow
  // for the selected type (Installed vs Workmanship both require a
  // holding but have different workflows and different capture fields).
  const { data: metadata, isLoading: metadataLoading } = useCreateClaimMetadata(
    state.claimType,
    {
      productTemplateName: state.holdingProductTemplateName,
      // Pass the holding uuid so the backend can resolve the workflow when the
      // tenant has no claim-type step (``claimType`` is null and the template
      // name alone cannot resolve a workflow). Template-name still keys the
      // cache for schema-driven flows; see createClaimMetadataQueryOptions.
      holdingUuid: state.holdingUuid,
    },
  )

  const createClaim = useCreateClaim()
  const [blockingError, setBlockingError] = useState<string | null>(null)
  // WPS-150: create-time potential-duplicate warning. The matches found by the
  // pre-check, and whether the informational pop-up is open.
  //
  // The check runs as EARLY as the wizard has the signals it needs (Patrick QA
  // 2026-09-24): on "Next" from Select Registration for holding-based types
  // (the holding's property is the signal), and on "Next" from Claim Details
  // for holdingless types (uninstalled / job-stop match on captured values).
  // Submit re-runs it only as a backstop (e.g. a deep link that skipped the
  // registration step). ``duplicateClearedSigRef`` records the input signature
  // that last came back clean or that the agent acknowledged with "Continue",
  // so the same input is never re-checked on a later step; changing the
  // registration, type, or holdingless signals re-runs it. Refs, not state, so
  // a continuation invoked straight after the dialog sees them synchronously.
  const [duplicateMatches, setDuplicateMatches] = useState<
    PotentialDuplicateClaim[]
  >([])
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
  const duplicateClearedSigRef = useRef<string | null>(null)
  const duplicatePendingRef = useRef<{
    sig: string
    proceed: () => void
  } | null>(null)
  const checkDuplicates = useCheckClaimDuplicates()
  const duplicatesEnabled = useFeatureFlag(FLAGS.DUPLICATES)
  // Blocks a double click from running the gate (and proceed) twice.
  const duplicateGateBusyRef = useRef(false)
  // The claim disclaimer + acknowledgement is shown ONCE, at the end of the
  // flow (Review & Submit). We deliberately consume only the ``claim_submit``
  // context here - the earlier ``claim_evidence_upload`` disclaimer was
  // removed so the notice + acknowledgement appear a single time.
  const { data: disclaimerData } = useClaimDisclaimers()
  const submitDisclaimer = disclaimerData?.disclaimers?.claim_submit ?? null
  const [submitAck, setSubmitAck] = useState(false)
  // Require a fresh acknowledgement if the agent goes back and changes
  // what is being claimed (different claim type or registration). The
  // duplicate-warning ack resets too, so changing the target re-runs the
  // pre-save duplicate check.
  useEffect(() => {
    setSubmitAck(false)
    setDuplicateMatches([])
  }, [state.claimType, state.holdingUuid])

  // The pre-check input for the current wizard state, plus a stable signature
  // of it. Holding-based claims pre-check off the holding (its property drives
  // the match, so captured values do not change the answer); holdingless types
  // (uninstalled / job-stop) pre-check off the captured claim-attribute values
  // (supplier / product, or the job-stop property address).
  const duplicateCheckInput = useMemo(() => {
    if (state.holdingUuid) {
      return {
        sig: `holding:${state.holdingUuid}:${state.claimType ?? ''}`,
        input: {
          holdingUuid: state.holdingUuid,
          claimSchemaKey: state.claimType ?? null,
          capturedValues: state.capturedValues,
        },
      }
    }
    if (state.claimType && !state.requiresHolding) {
      return {
        sig: `values:${state.claimType}:${JSON.stringify(state.capturedValues)}`,
        input: {
          claimSchemaKey: state.claimType,
          capturedValues: state.capturedValues,
        },
      }
    }
    return null
  }, [
    state.holdingUuid,
    state.claimType,
    state.requiresHolding,
    state.capturedValues,
  ])

  // Run the potential-duplicate pre-check, then ``proceed``. When siblings
  // match, show the informational pop-up instead and park ``proceed`` so
  // "Continue Creating Claim" resumes exactly where the agent was. Never a hard
  // block: a failed check, the ``duplicates`` flag being off (endpoint returns
  // []), or an input already cleared/acknowledged all proceed immediately.
  const runDuplicateGate = useCallback(
    async (proceed: () => void) => {
      if (duplicateGateBusyRef.current) return
      const current = duplicateCheckInput
      if (!current || duplicateClearedSigRef.current === current.sig) {
        proceed()
        return
      }
      if (duplicatesEnabled) {
        duplicateGateBusyRef.current = true
        try {
          const matches = await checkDuplicates.mutateAsync(current.input)
          if (matches.length > 0) {
            setDuplicateMatches(matches)
            duplicatePendingRef.current = { sig: current.sig, proceed }
            setDuplicateDialogOpen(true)
            return
          }
        } catch {
          // Never let a failed pre-check strand the claim - proceed.
        } finally {
          duplicateGateBusyRef.current = false
        }
      }
      // Clear before proceeding: the submit backstop re-enters the gate until
      // the signature is cleared (flag off or a failed check looped forever).
      duplicateClearedSigRef.current = current.sig
      proceed()
    },
    [duplicateCheckInput, checkDuplicates, duplicatesEnabled],
  )

  // Step-level "Next" that runs the duplicate gate first. Wired to Select
  // Registration (holding-based types) and Claim Details (holdingless types,
  // and a no-op re-check for holding types whose signature already cleared).
  const nextWithDuplicateCheck = useCallback(() => {
    void runDuplicateGate(next)
  }, [runDuplicateGate, next])

  const handleSubmit = useCallback(async () => {
    if (state.requiresHolding && !state.holdingUuid) {
      toast.error('Please select a registration')
      return
    }
    if (!state.requiresHolding && !state.claimType) {
      toast.error('Please select a claim type')
      return
    }

    // WPS-150: backstop duplicate check before persisting. Normally already
    // cleared on an earlier step (same signature -> no request); covers deep
    // links that skipped the registration step. Re-invokes submit on Continue.
    if (
      duplicateCheckInput &&
      duplicateClearedSigRef.current !== duplicateCheckInput.sig
    ) {
      void runDuplicateGate(() => void handleSubmitRef.current())
      return
    }

    try {
      // Auto-inject claim_type from step 1 so the user isn't asked twice
      const mergedValues = {
        ...state.capturedValues,
        ...(state.claimType ? { claim_type: state.claimType } : {}),
        // Record the agent's acknowledgement (read out over the phone) as data.
        ...(submitDisclaimer && submitAck
          ? { disclaimer_acknowledged: disclaimerAckValue(submitDisclaimer) }
          : {}),
      }
      const result = await createClaim.mutateAsync({
        holding_uuid: state.holdingUuid,
        // Always send claim_schema_key so the backend resolves the
        // correct workflow - Installed and Workmanship both use a
        // holding but have different schemas and workflows.
        claim_schema_key: state.claimType ?? null,
        captured_values: mergedValues,
      })
      // Clear the resumable draft now the claim is submitted.
      setSubmitted(true)
      clearWizardCache(cacheKey)
      toast.success('Claim submitted successfully')
      // Navigate to the new claim detail (on Overview) if uuid is available
      const newUuid = (result as Record<string, unknown>)?.uuid
      navigate(newUuid ? `/claims/${newUuid}?tab=overview` : '/claims')
    } catch (err: unknown) {
      // Extract the backend message. If it's a domain/validation rule error
      // (i.e. the backend returned a meaningful message, not just a generic
      // HTTP error), surface it as a prominent blocking dialog instead of a
      // corner toast so the agent doesn't miss critical routing instructions.
      const extracted = extractApiErrorMessage(err)
      if (extracted) {
        setBlockingError(extracted)
      } else {
        toastApiError(err, 'Failed to submit claim')
      }
    }
  }, [
    state,
    createClaim,
    duplicateCheckInput,
    runDuplicateGate,
    navigate,
    submitDisclaimer,
    submitAck,
    cacheKey,
  ])

  // Latest submit handler, so the gate's parked continuation never calls a
  // stale closure.
  const handleSubmitRef = useRef(handleSubmit)
  useLayoutEffect(() => {
    handleSubmitRef.current = handleSubmit
  }, [handleSubmit])

  // "Continue Creating Claim" from the duplicate-warning pop-up: record the
  // acknowledgement for this input and resume where the agent was (the next
  // step, or the submit).
  const handleContinueDespiteDuplicates = useCallback(() => {
    const pending = duplicatePendingRef.current
    duplicatePendingRef.current = null
    setDuplicateDialogOpen(false)
    if (!pending) return
    duplicateClearedSigRef.current = pending.sig
    pending.proceed()
  }, [])

  const stepContent = useMemo(() => {
    switch (currentStepKey) {
      case 'claim_type':
        return (
          <StepClaimType
            update={update}
            next={next}
            onSelectUmbrella={
              multiItemEnabled
                ? (key) =>
                    navigate(
                      `/claims/submit-multi?type=${encodeURIComponent(key)}`,
                    )
                : bulkImportsEnabled
                  ? (key) => {
                      setBulkImportType(key)
                      setBulkImportOpen(true)
                    }
                  : undefined
            }
          />
        )
      case 'holding':
        return (
          <StepSelectHolding
            state={state}
            update={update}
            next={nextWithDuplicateCheck}
          />
        )
      case 'details':
        return (
          <StepClaimDetails
            state={state}
            update={update}
            next={nextWithDuplicateCheck}
            back={preselectedHoldingUuid ? () => navigate(-1) : back}
            fields={metadata?.fields ?? []}
            prefill={metadata?.prefill ?? {}}
            isLoading={metadataLoading}
          />
        )
      case 'review':
        return (
          <StepReview
            state={state}
            back={back}
            fields={metadata?.fields ?? []}
            onSubmit={handleSubmit}
            isSubmitting={createClaim.isPending}
            disclaimer={submitDisclaimer}
            acknowledged={submitAck}
            onAcknowledgedChange={setSubmitAck}
          />
        )
      default:
        return null
    }
  }, [currentStepKey, state, update, next, nextWithDuplicateCheck, back, metadata, metadataLoading, handleSubmit, createClaim.isPending, navigate, preselectedHoldingUuid, bulkImportsEnabled, multiItemEnabled, submitDisclaimer, submitAck])

  // Flag-driven single-vs-multi fork. Shown when the multi_item_claims
  // capability is on and the tenant has at least one umbrella type - before
  // the normal wizard, and independent of claim_type options. Skipped when a
  // holding/claim type is preselected (a deep link into a specific flow).
  const showMultiItemFork =
    multiItemEnabled &&
    multiItemTypes.length > 0 &&
    !forkResolved &&
    !preselectedHoldingUuid &&
    !preselectedClaimType

  const claimTypeLabel = claimTypeData?.options?.find(
    (o) => o.value === state.claimType,
  )?.label

  if (showMultiItemFork) {
    return (
      <AppShell>
        <div className="min-h-[calc(100vh-48px)] bg-[var(--bg)]">
          <div className="page-container py-10">
            <h1 className="m-0 mb-1 font-display text-[32px] leading-[1.1] text-[var(--ink)]">
              {t('claims.submitClaim', 'Submit Claim')}
            </h1>
            <p className="m-0 mb-8 text-[13px] text-[var(--ink-3)]">
              Are you claiming for a single item or several?
            </p>
            <div className="grid max-w-[640px] grid-cols-1 gap-4 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setForkResolved(true)}
                className="flex flex-col items-start gap-2 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5 text-left transition-colors hover:border-[var(--rule-strong)]"
              >
                <Package className="size-5 text-[var(--ink-2)]" />
                <span className="text-[15px] text-[var(--ink)]">One item</span>
                <span className="text-[12px] text-[var(--ink-3)]">
                  File a claim for a single registration.
                </span>
              </button>
              {multiItemTypes.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() =>
                    navigate(
                      `/claims/submit-multi?type=${encodeURIComponent(t.key)}`,
                    )
                  }
                  className="flex flex-col items-start gap-2 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5 text-left transition-colors hover:border-[var(--rule-strong)]"
                >
                  <Layers className="size-5 text-[var(--ink-2)]" />
                  <span className="text-[15px] text-[var(--ink)]">
                    Several items
                  </span>
                  <span className="text-[12px] text-[var(--ink-3)]">
                    {t.label} - one claim grouping each item.
                  </span>
                </button>
              ))}
            </div>
            <Button
              variant="link"
              onClick={() => {
                clearWizardCache(cacheKey)
                navigate('/claims')
              }}
              className="mt-8 h-auto p-0 text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              Cancel
            </Button>
          </div>
        </div>
      </AppShell>
    )
  }

  return (
    <ResourceLookupContextProvider providers={resourceLookupProviders}>
    <AddressLookupProvider api={addressLookupApi}>
    <AppShell>
      <div
        className="min-h-[calc(100vh-48px)] flex flex-col bg-[var(--bg)]"
      >
        {/* Header */}
        <div className="border-b-[0.5px] border-[var(--rule)]">
          <div className="page-container pb-4 pt-5">
            <div className="mb-4 flex items-center justify-between">
              <h1 className="m-0 text-[32px] leading-[1.1] text-[var(--ink)] font-display">
                {t('claims.submitClaim', 'Submit Claim')}
                {claimTypeLabel ? ` - ${claimTypeLabel}` : ''}
              </h1>
              <div className="flex items-center gap-4">
                <ConfigHelp
                  sections={configSectionsForClaimStep(currentStepKey)}
                  returnLabel={CLAIM_RETURN_LABEL}
                />
                <Button
                  onClick={() => {
                    // Discard the in-progress draft so cancelling starts fresh
                    // next time instead of silently resuming.
                    clearWizardCache(cacheKey)
                    navigate('/claims')
                  }}
                  variant="link"
                  className="text-[11px] font-normal uppercase tracking-[0.1em] cursor-pointer transition-all duration-150 p-0 h-auto text-[var(--ink-3)] hover:text-[var(--ink)]"
                >
                  Cancel
                </Button>
              </div>
            </div>

            {/* Inline stepper - renders only the visible step list
                so tenants without a ``claim_type`` attribute see
                three pips instead of four. */}
            <div className="flex max-w-[600px] items-center">
              {visibleStepLabels.map((label, i) => (
                <div key={label} className="flex items-center">
                  <Button
                    onClick={() => {
                      if (i < currentStepIndex && !(i <= 1 && preselectedHoldingUuid)) {
                        dispatch({ type: 'GO_TO', step: i })
                      }
                    }}
                    variant="ghost"
                    className={cn(
                      'flex h-auto items-center gap-1.5 p-0 transition-all duration-150',
                      i < currentStepIndex && !(i <= 1 && preselectedHoldingUuid) ? 'cursor-pointer' : 'cursor-default'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[9px] font-normal',
                        i <= currentStepIndex
                          ? 'bg-[var(--green)] text-white'
                          : 'border-[0.5px] border-[var(--rule-strong)] bg-transparent text-[var(--ink-3)]'
                      )}
                    >
                      {i < currentStepIndex ? '\u2713' : i + 1}
                    </span>
                    <span
                      className={cn(
                        "hidden sm:inline whitespace-nowrap text-[10px] uppercase tracking-[0.12em]",
                        i <= currentStepIndex ? 'text-[var(--ink)]' : 'text-[var(--ink-3)]'
                      )}
                    >
                      {label}
                    </span>
                  </Button>
                  {i < visibleStepLabels.length - 1 && (
                    <div className="mx-1 sm:mx-2 h-[0.5px] w-3 sm:w-6 shrink-0 bg-[var(--rule-strong)]" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="page-container py-5">
            {(() => {
              const section = primaryConfigSectionForClaimStep(currentStepKey)
              return section ? (
                <div className="mb-3 flex justify-end">
                  <EditConfigLink
                    section={section}
                    returnLabel={CLAIM_RETURN_LABEL}
                  />
                </div>
              ) : null
            })()}
            {stepContent}
          </div>
        </div>
      </div>

      {/* Blocking validation dialog - shown when the backend returns a
          domain-level error that the agent must act on (e.g. transfer caller
          instructions). Rendered prominently in the centre of the screen
          rather than as a corner toast. */}
      <Dialog open={!!blockingError} onOpenChange={(open) => { if (!open) setBlockingError(null) }}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
              Action Required
            </DialogTitle>
            <DialogDescription className="mt-2 text-[13px] leading-[1.6] text-[var(--ink-2)]">
              {blockingError}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              variant="default"
              onClick={() => setBlockingError(null)}
              className="cursor-pointer rounded-[6px] bg-[var(--green)] px-5 text-[10px] font-normal uppercase tracking-[0.12em] text-white"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* WPS-150: create-time potential-duplicate warning (shared component).
          Informational only - it never blocks. Continue acknowledges and
          re-submits; Cancel returns to the review step. */}
      <PotentialDuplicateClaimDialog
        open={duplicateDialogOpen}
        onOpenChange={setDuplicateDialogOpen}
        matches={duplicateMatches}
        onContinue={handleContinueDespiteDuplicates}
        isContinuing={createClaim.isPending}
      />
      {bulkImportsEnabled && bulkImportType && (
        <BulkImportDialog
          open={bulkImportOpen}
          onOpenChange={setBulkImportOpen}
          target={`claim:${bulkImportType}`}
          title="Bulk Upload Claims"
          onComplete={() => navigate('/claims')}
        />
      )}
    </AppShell>
    </AddressLookupProvider>
    </ResourceLookupContextProvider>
  );
}

// ── Step 0: Claim Type ──────────────────────────────────


function StepClaimType({
  update,
  next,
  onSelectUmbrella,
}: {
  update: (payload: Partial<WizardState>) => void
  next: () => void
  /** When set, clicking an umbrella claim type calls this instead of
   * advancing the single-claim wizard (routes into the bulk importer). */
  onSelectUmbrella?: (claimTypeKey: string) => void
}) {
  const { data, isLoading } = useClaimTypeOptions()
  const options: ClaimTypeOption[] = data?.options ?? []
  return (
    <div>
      <p className="mb-5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
        Select a claim type
      </p>
      {isLoading && options.length === 0 ? (
        <p className="text-[12px] text-[var(--ink-3)]">Loading claim types…</p>
      ) : (
        <div data-tour="claim-type-selector" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {options.map((ct) => {
            const Icon = resolveClaimTypeIcon(ct.icon)
            return (
              <button
                key={ct.value}
                disabled={!ct.active}
                onClick={() => {
                  if (!ct.active) return
                  // Umbrella types (e.g. HOA) file as one parent + a child
                  // claim per unit - route into the bulk importer (the single
                  // consistent path) instead of the single-claim wizard.
                  if (ct.umbrella && onSelectUmbrella) {
                    onSelectUmbrella(ct.value)
                    return
                  }
                  update({
                    claimType: ct.value,
                    requiresHolding: ct.requires_holding,
                    createsRegistration: Boolean(ct.creates_registration),
                    // Pre-set claim_type in capturedValues so
                    // visibility predicates that depend on it work
                    // in the details form (the dropdown is hidden
                    // since the user already picked the type here).
                    capturedValues: { claim_type: ct.value },
                    // Record the product template to filter when opening
                    // the "New Registration" wizard from the holding step.
                    productTemplateName: ct.product_template_name || null,
                    // Clear holding when switching to a holdingless type
                    ...(!ct.requires_holding
                      ? {
                          holdingUuid: null,
                          holdingDisplay: null,
                          holdingProductTemplateName: null,
                        }
                      : {}),
                  })
                  next()
                }}
                className={cn(
                  'group relative flex flex-col items-start gap-4 rounded-[8px] border-[0.5px] p-5 text-left transition-all duration-150',
                  ct.active
                    ? 'cursor-pointer border-[var(--rule)] bg-[var(--bg-subtle)] hover:-translate-y-0.5 hover:border-[var(--rule-strong)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)]'
                    : 'cursor-default border-[var(--rule)] bg-[var(--bg-subtle)] opacity-55',
                )}
              >
                {!ct.active && (
                  <Badge
                    variant="secondary"
                    className="absolute right-3 top-3 gap-1 bg-[var(--bg)] px-2 py-0.5 text-[9px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)]"
                  >
                    <CircleDashed size={10} />
                    Coming Soon
                  </Badge>
                )}
                <span
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-[6px]',
                    ct.active
                      ? 'bg-[var(--green-bg)] text-[var(--green)]'
                      : 'bg-[var(--bg)] text-[var(--ink-3)]',
                  )}
                >
                  <Icon size={18} />
                </span>
                <div>
                  <p
                    className={cn(
                      'mb-1 text-[13px] font-normal uppercase tracking-[0.08em]',
                      ct.active ? 'text-[var(--ink)]' : 'text-[var(--ink-3)]',
                    )}
                  >
                    {ct.label}
                  </p>
                  <p className="text-[12px] leading-[1.4] text-[var(--ink-3)]">
                    {ct.description}
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Step 1: Select Registration ──────────────────────────

interface HoldingRow {
  uuid: string
  registration_number?: string | null
  product?: {
    cached_short_name?: string
    product_type?: string
    product_template?: { name?: string } | null
    category?: { name?: string } | null
    parent_product?: { cached_short_name?: string } | null
  } | null
  consumer_account?: {
    uuid?: string
    first_name?: string
    last_name?: string
    email?: string
  } | null
  host?: {
    registration_number?: string
    name?: string
    address_line_1?: string
    address_line_2?: string
    address_town_or_city?: string
    address_county?: string
    address_postcode?: string
    address_country?: string
    address?: { display?: string } | null
    display_name?: string
    attribute_values?: { code_name: string; value: unknown }[]
  } | null
  structure?: {
    uuid: string
    label: string
    attribute_values?: Record<string, unknown>
  } | null
  warranties?: { status?: string }[]
  created_at: string
}

function StepSelectHolding({
  state,
  update,
  next,
}: {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  next: () => void
}) {
  const { data: registrationSettings } = useRegistrationSettings()
  const mode = registrationSettings?.claims_search_mode ?? 'direct'

  return mode === 'host_first' ? (
    <StepSelectHoldingHostFirst state={state} update={update} next={next} />
  ) : (
    <StepSelectHoldingDirect state={state} update={update} next={next} />
  )
}

// \u2500\u2500 Direct registration search (default) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

function StepSelectHoldingDirect({
  state,
  update,
  next,
}: {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  next: () => void
}) {
  const navigate = useNavigate()
  const { locale } = useFormatting()
  const { data: registrationSettings } = useRegistrationSettings()
  const showProductVariant =
    registrationSettings?.show_product_variant_at_claim_intake ?? true
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 10
  const params = useMemo(() => {
    const p: Record<string, string> = { page: String(page), page_size: String(PAGE_SIZE) }
    if (search) p.search = search
    return p
  }, [search, page])

  const { data: holdingsData, isLoading } = useHoldings(params)
  const holdings = (holdingsData?.results ?? []) as HoldingRow[]
  const totalCount = (holdingsData as { count?: number })?.count ?? 0
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  const handleSearchChange = (val: string) => {
    setSearch(val)
    setPage(1)
  }

  const handleSelect = (h: HoldingRow) => {
    update({
      holdingUuid: h.uuid,
      holdingDisplay: buildProductDisplay(h.product, showProductVariant),
      holdingRegistrationNumber: h.registration_number ?? null,
      holdingProductTemplateName: h.product?.product_template?.name ?? null,
    })
  }

  // HOA claims anchor to the registered HOA community. When no community
  // is registered yet, the "Register HOA Community" button routes to the
  // registration wizard with structure type pre-set to Other / Homeowners
  // Association and carries claim_type=hoa so the round-trip stays HOA.
  const isHoa = state.claimType === 'hoa'
  const handleRegister = () => {
    navigate('/registrations/new', {
      state: isHoa
        ? {
            returnTo: '/claims/submit?claim_type=hoa',
            propertyRegistrationPrefill: {
              // structure_type is a host attribute; other_structure_type is
              // a tenant RegistrationField, so each goes in its own slot.
              hostAttributeValues: { structure_type: 'Other' },
              registrationFieldValues: {
                other_structure_type: 'homeowners_association',
              },
            },
          }
        : {
            returnTo: '/claims/submit',
            productTemplateName: state.productTemplateName || undefined,
            claimSchemaKey: state.claimType || undefined,
          },
    })
  }

  return (
    <div>
      <div data-tour="registration-selector" className="flex w-full max-w-[720px] flex-col rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
        {/* Header */}
        <p className="border-b-[0.5px] border-[var(--rule)] px-4 py-3 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
          {isHoa ? 'Select the HOA community' : 'Select a registration'}
        </p>

        {/* Search */}
        <div className="border-b-[0.5px] border-[var(--rule)] px-4 py-2.5">
          <SearchInput
            placeholder={
              isHoa
                ? 'Search the registered HOA community by address, name, or Reg #...'
                : 'Search by address, postcode, owner, product, or Reg #...'
            }
            value={search}
            onChange={handleSearchChange}
            className="h-8 text-[13px]"
          />
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between border-b-[0.5px] border-[var(--rule)] px-4 py-2">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {isLoading ? '...' : `${totalCount} registration${totalCount !== 1 ? 's' : ''}`}
          </span>
          <Button
            onClick={handleRegister}
            variant="ghost"
            className="h-auto cursor-pointer gap-1 p-0 text-[11px] font-normal uppercase tracking-[0.08em] text-[var(--green)] hover:text-[var(--ink)]"
          >
            <Plus size={12} />
            {isHoa ? 'Register HOA Community' : 'New Registration'}
          </Button>
        </div>

        {/* Holdings list */}
        <div className="clara-scroll max-h-[420px] overflow-y-auto">
          {isLoading ? (
            <p className="px-4 py-5 text-[13px] text-[var(--ink-3)]">
              Loading registrations...
            </p>
          ) : holdings.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="mb-3 text-[13px] text-[var(--ink-3)]">
                No registrations match your search
              </p>
              <Button
                onClick={() => navigate('/registrations/new', { state: { returnTo: '/claims/submit', productTemplateName: state.productTemplateName || undefined, claimSchemaKey: state.claimType || undefined } })}
                variant="outline"
                className="cursor-pointer gap-1.5 rounded-[6px] border-[0.5px] border-[var(--green)] px-4 py-2 text-[11px] font-normal uppercase tracking-[0.08em] text-[var(--green)] hover:bg-[var(--green-bg)]"
              >
                <Plus size={14} />
                Register New Product
              </Button>
            </div>
          ) : (
            holdings.map((h, i) => {
              const isSelected = state.holdingUuid === h.uuid
              const variantName = h.product?.cached_short_name || 'Unknown Product'
              const parentName = h.product?.parent_product?.cached_short_name
              const templateName = h.product?.product_template?.name
              const customer = h.consumer_account
              const customerName = customer
                ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email
                : '-'
              const dateStr = new Date(h.created_at).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })

              const host = h.host
              const addressParts = [
                host?.address_line_1,
                host?.address_line_2,
                host?.address_town_or_city,
                host?.address_postcode,
              ].filter((p): p is string => !!p?.trim())
              const addressLine = addressParts.join(', ')
              const hostLabel = host?.name || addressLine || null

              return (
                <Button
                  key={h.uuid}
                  variant="ghost"
                  onClick={() => handleSelect(h)}
                  data-selected={isSelected || undefined}
                  className={cn(
                    'flex h-auto min-h-[44px] w-full cursor-pointer items-center justify-start gap-3 rounded-none border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-0 px-4 py-3 text-left font-normal transition-all duration-150 data-[selected]:bg-[var(--green-bg)] hover:not-data-[selected]:bg-[var(--bg)]',
                    i === holdings.length - 1 ? 'border-b-0' : '',
                  )}
                >
                  {/* Radio circle */}
                  <span
                    className={cn(
                      'flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full border-[0.5px] transition-all duration-150',
                      isSelected
                        ? 'border-[var(--green)] bg-[var(--green)]'
                        : 'border-[var(--rule-strong)] bg-white',
                    )}
                  >
                    {isSelected && (
                      <span className="h-[5px] w-[5px] rounded-full bg-white" />
                    )}
                  </span>
                  {/* Content */}
                  <div className="flex flex-1 items-center justify-between">
                    <div className="flex flex-col gap-0.5">
                      {hostLabel && (
                        <span className={cn('text-[13px] transition-colors duration-150', isSelected ? 'font-normal text-[var(--green)]' : 'text-[var(--ink)]')}>
                          {hostLabel}
                        </span>
                      )}
                      <div className="flex items-center gap-2">
                        <span className={cn('transition-colors duration-150', hostLabel ? 'text-[11px] text-[var(--ink-3)]' : cn('text-[13px]', isSelected ? 'font-normal text-[var(--green)]' : 'text-[var(--ink)]'))}>
                          {parentName || variantName}
                          {showProductVariant && parentName && variantName !== parentName
                            ? ` \u00b7 ${variantName}`
                            : ''}
                        </span>
                        {templateName && (
                          <span className="text-[10px] uppercase tracking-[0.05em] text-[var(--ink-3)]">
                            {templateName}
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-[var(--ink-3)]">
                        {customerName}
                      </span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-[10px] text-[var(--ink-3)]">
                        {dateStr}
                      </span>
                      {h.registration_number && (
                        <span className="font-mono text-[10px] text-[var(--ink-3)]">
                          {h.registration_number}
                        </span>
                      )}
                    </div>
                  </div>
                </Button>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 border-t-[0.5px] border-[var(--rule)] py-2.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="text-[11px] text-[var(--ink-3)]"
            >
              Previous
            </Button>
            <span className="text-[11px] text-[var(--ink-3)]">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="text-[11px] text-[var(--ink-3)]"
            >
              Next
            </Button>
          </div>
        )}
      </div>
      {/* Continue button */}
      <div className="mt-2 flex justify-end sm:col-span-2">
        <Button
          onClick={next}
          disabled={!state.holdingUuid}
          variant="default"
          className={cn(
            'rounded-[6px] px-6 py-2.5 text-[12px] font-normal uppercase tracking-[0.08em]',
            state.holdingUuid
              ? 'bg-[var(--green)] text-white'
              : 'cursor-not-allowed bg-[var(--bg-subtle)] text-[var(--ink-3)]'
          )}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

// \u2500\u2500 Host-first search (OC / property tenants) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

function StepSelectHoldingHostFirst({
  state,
  update,
  next,
}: {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  next: () => void
}) {
  const navigate = useNavigate()
  const { locale } = useFormatting()

  const { data: registrationSettings } = useRegistrationSettings()
  const showProductVariant =
    registrationSettings?.show_product_variant_at_claim_intake ?? true
  const hostNoun = hostNounForDetailView(
    registrationSettings?.registration_detail_view,
  )

  const [propertySearch, setPropertySearch] = useState('')
  const [selectedHost, setSelectedHost] = useState<HostSearchResult | null>(null)

  const { data: hostResults, isLoading: hostsLoading } = useHostSearch(
    propertySearch,
    { limit: 20 },
  )
  const hosts = hostResults?.results ?? []

  const [page, setPage] = useState(1)
  const PAGE_SIZE = 10
  const holdingParams = useMemo(() => {
    if (!selectedHost) return undefined
    return {
      host_uuid: selectedHost.uuid,
      page: String(page),
      page_size: String(PAGE_SIZE),
    }
  }, [selectedHost, page])

  const { data: holdingsData, isLoading: holdingsLoading } = useHoldings(holdingParams)
  const holdings = (holdingsData?.results ?? []) as HoldingRow[]
  const totalCount = (holdingsData as { count?: number })?.count ?? 0
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  const handleSelectHost = (host: HostSearchResult) => {
    setSelectedHost(host)
    setPage(1)
    update({ holdingUuid: null, holdingDisplay: null })
  }

  const handleClearHost = () => {
    setSelectedHost(null)
    setPropertySearch('')
    update({ holdingUuid: null, holdingDisplay: null })
  }

  const handleSelectHolding = (h: HoldingRow) => {
    update({
      holdingUuid: h.uuid,
      holdingDisplay: buildProductDisplay(h.product, showProductVariant),
      holdingRegistrationNumber: h.registration_number ?? null,
      holdingProductTemplateName: h.product?.product_template?.name ?? null,
    })
  }

  return (
    <div>
      <div data-tour="registration-selector" className="flex w-full max-w-[720px] flex-col rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
        {/* Header */}
        <p className="border-b-[0.5px] border-[var(--rule)] px-4 py-3 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
          {selectedHost ? 'Select a registration' : `Search for a ${hostNoun.singular}`}
        </p>

        {!selectedHost ? (
          <>
            {/* Host search input */}
            <div className="border-b-[0.5px] border-[var(--rule)] px-4 py-2.5">
              <SearchInput
                placeholder="Search by address, postcode, owner, or product..."
                value={propertySearch}
                onChange={setPropertySearch}
                className="h-8 text-[13px]"
              />
            </div>

            {/* Host results */}
            <div className="clara-scroll max-h-[420px] overflow-y-auto">
              {propertySearch.trim().length < 2 ? (
                <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg)]">
                    <MapPin size={18} className="text-[var(--ink-3)]" />
                  </span>
                  <p className="text-[13px] text-[var(--ink-3)]">
                    Search for a {hostNoun.singular} to get started
                  </p>
                  <p className="text-[11px] text-[var(--ink-3)]/60">
                    {hostNoun.example}
                  </p>
                </div>
              ) : hostsLoading ? (
                <p className="px-4 py-5 text-[13px] text-[var(--ink-3)]">
                  Searching {hostNoun.plural}...
                </p>
              ) : hosts.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <p className="mb-3 text-[13px] text-[var(--ink-3)]">
                    No {hostNoun.plural} match your search
                  </p>
                  <Button
                    onClick={() => navigate('/registrations/new', { state: { returnTo: '/claims/submit' } })}
                    variant="outline"
                    className="cursor-pointer gap-1.5 rounded-[6px] border-[0.5px] border-[var(--green)] px-4 py-2 text-[11px] font-normal uppercase tracking-[0.08em] text-[var(--green)] hover:bg-[var(--green-bg)]"
                  >
                    <Plus size={14} />
                    Register New Product
                  </Button>
                </div>
              ) : (
                hosts.map((host, i) => {
                  const addressDisplay = host.address?.display || host.display_name
                  return (
                    <button
                      key={host.uuid}
                      onClick={() => handleSelectHost(host)}
                      className={cn(
                        'flex min-h-[44px] w-full cursor-pointer items-center gap-3 border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3 text-left transition-all duration-150 hover:bg-[var(--bg)]',
                        i === hosts.length - 1 ? 'border-b-0' : '',
                      )}
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] bg-[var(--bg)]">
                        <Home size={14} className="text-[var(--ink-3)]" />
                      </span>
                      <div className="flex flex-1 items-center justify-between">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[13px] text-[var(--ink)]">
                            {addressDisplay}
                          </span>
                          {host.current_owner_name && (
                            <span className="text-[10px] text-[var(--ink-3)]">
                              {host.current_owner_name}
                            </span>
                          )}
                        </div>
                        <span className="shrink-0 text-[10px] text-[var(--ink-3)]">
                          {host.active_holding_count} registration{host.active_holding_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          </>
        ) : (
          <>
            {/* Selected host header with back button */}
            <div className="flex items-center gap-2 border-b-[0.5px] border-[var(--rule)] px-4 py-2.5">
              <Button
                onClick={handleClearHost}
                variant="ghost"
                className="h-auto cursor-pointer gap-1 p-0 text-[11px] font-normal text-[var(--ink-3)] hover:text-[var(--ink)]"
              >
                <ChevronLeft size={14} />
              </Button>
              <div className="flex flex-1 items-center gap-2">
                <Home size={14} className="shrink-0 text-[var(--green)]" />
                <span className="text-[13px] text-[var(--ink)]">
                  {selectedHost.address?.display || selectedHost.display_name}
                </span>
              </div>
            </div>

            {/* Action bar */}
            <div className="flex items-center justify-between border-b-[0.5px] border-[var(--rule)] px-4 py-2">
              <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                {holdingsLoading ? '...' : `${totalCount} registration${totalCount !== 1 ? 's' : ''}`}
              </span>
              <Button
                onClick={() => navigate('/registrations/new', { state: { returnTo: '/claims/submit' } })}
                variant="ghost"
                className="h-auto cursor-pointer gap-1 p-0 text-[11px] font-normal uppercase tracking-[0.08em] text-[var(--green)] hover:text-[var(--ink)]"
              >
                <Plus size={12} />
                New Registration
              </Button>
            </div>

            {/* Holdings list for the selected host */}
            <div className="clara-scroll max-h-[420px] overflow-y-auto">
              {holdingsLoading ? (
                <p className="px-4 py-5 text-[13px] text-[var(--ink-3)]">
                  Loading registrations...
                </p>
              ) : holdings.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <p className="mb-3 text-[13px] text-[var(--ink-3)]">
                    No registrations found
                  </p>
                  <Button
                    onClick={() => navigate('/registrations/new', { state: { returnTo: '/claims/submit' } })}
                    variant="outline"
                    className="cursor-pointer gap-1.5 rounded-[6px] border-[0.5px] border-[var(--green)] px-4 py-2 text-[11px] font-normal uppercase tracking-[0.08em] text-[var(--green)] hover:bg-[var(--green-bg)]"
                  >
                    <Plus size={14} />
                    Register New Product
                  </Button>
                </div>
              ) : (
                holdings.map((h, i) => {
                  const isSelected = state.holdingUuid === h.uuid
                  const variantName = h.product?.cached_short_name || 'Unknown Product'
                  const parentName = h.product?.parent_product?.cached_short_name
                  const templateName = h.product?.product_template?.name
                  const customer = h.consumer_account
                  const customerName = customer
                    ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email
                    : '-'
                  const dateStr = new Date(h.created_at).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
                  const propertyLabel = h.host?.address?.display || h.host?.display_name || ''
                  const structureLabel = (() => {
                    if (!h.structure) return ''
                    const attrs = h.structure.attribute_values
                    const get = (key: string): string => {
                      if (Array.isArray(attrs)) {
                        return (attrs.find((a: { code_name?: string; value?: unknown }) => a?.code_name === key)?.value as string) || ''
                      }
                      return (attrs?.[key] as string) || ''
                    }
                    const sName = get('structure_name')
                    if (sName) return sName
                    if (h.structure.label) return h.structure.label
                    const sType = get('structure_type')
                    if (sType === 'Other') return get('other_structure_type') || sType
                    return sType || 'Structure'
                  })()

                  return (
                    <button
                      key={h.uuid}
                      onClick={() => handleSelectHolding(h)}
                      data-selected={isSelected || undefined}
                      className={cn(
                        'flex min-h-[44px] w-full cursor-pointer items-center gap-3 border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3 text-left transition-all duration-150 data-[selected]:bg-[var(--green-bg)] hover:not-data-[selected]:bg-[var(--bg)]',
                        i === holdings.length - 1 ? 'border-b-0' : '',
                      )}
                    >
                      {/* Radio circle */}
                      <span
                        className={cn(
                          'flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full border-[0.5px] transition-all duration-150',
                          isSelected
                            ? 'border-[var(--green)] bg-[var(--green)]'
                            : 'border-[var(--rule-strong)] bg-white',
                        )}
                      >
                        {isSelected && (
                          <span className="h-[5px] w-[5px] rounded-full bg-white" />
                        )}
                      </span>
                      {/* Content */}
                      <div className="flex flex-1 items-center justify-between">
                        <div className="flex flex-col gap-0.5">
                          {propertyLabel && (
                            <span className={cn('text-[13px] transition-colors duration-150', isSelected ? 'font-normal text-[var(--green)]' : 'text-[var(--ink)]')}>
                              {propertyLabel}
                            </span>
                          )}
                          <div className="flex items-center gap-2">
                            <span className={cn('transition-colors duration-150', propertyLabel ? 'text-[11px] text-[var(--ink-3)]' : cn('text-[13px]', isSelected ? 'font-normal text-[var(--green)]' : 'text-[var(--ink)]'))}>
                              {parentName || variantName}
                              {showProductVariant && parentName && variantName !== parentName
                                ? ` \u00b7 ${variantName}`
                                : ''}
                            </span>
                            {templateName && (
                              <span className="text-[10px] uppercase tracking-[0.05em] text-[var(--ink-3)]">
                                {templateName}
                              </span>
                            )}
                          </div>
                          {structureLabel && (
                            <div className="flex items-center gap-1.5">
                              <Badge variant="outline" className="gap-1 px-1.5 py-0 text-[10px] font-normal text-[var(--ink-2)]">
                                <Home size={10} className="text-[var(--ink-3)]" />
                                {structureLabel}
                              </Badge>
                            </div>
                          )}
                          <span className="text-[10px] text-[var(--ink-3)]">
                            {customerName}
                          </span>
                        </div>
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="text-[10px] text-[var(--ink-3)]">
                            {dateStr}
                          </span>
                          <span className="font-mono text-[10px] text-[var(--ink-3)]">
                            {formatRegistrationReference(h)}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 border-t-[0.5px] border-[var(--rule)] py-2.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="text-[11px] text-[var(--ink-3)]"
                >
                  Previous
                </Button>
                <span className="text-[11px] text-[var(--ink-3)]">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="text-[11px] text-[var(--ink-3)]"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      {/* Continue button */}
      <div className="mt-2 flex justify-end sm:col-span-2">
        <Button
          onClick={next}
          disabled={!state.holdingUuid}
          variant="default"
          className={cn(
            'rounded-[6px] px-6 py-2.5 text-[12px] font-normal uppercase tracking-[0.08em]',
            state.holdingUuid
              ? 'bg-[var(--green)] text-white'
              : 'cursor-not-allowed bg-[var(--bg-subtle)] text-[var(--ink-3)]'
          )}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

// ── Step 2: Claim Details ────────────────────────────────

function StepClaimDetails({
  state,
  update,
  next,
  back,
  fields,
  prefill,
  isLoading,
}: {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  next: () => void
  back: () => void
  fields: ActionFieldMeta[]
  prefill: Record<string, unknown>
  isLoading: boolean
}) {
  const dependencyGraph = useMemo(() => {
    const graph: Record<string, string[]> = {}
    for (const field of fields) {
      const dep = field.configuration_data?.depends_on as string | undefined
      if (dep) {
        if (!graph[dep]) graph[dep] = []
        graph[dep].push(field.code_name)
      }
    }
    return graph
  }, [fields])

  const collectDependents = useCallback(
    (fieldName: string): string[] => {
      const result: string[] = []
      const stack = [...(dependencyGraph[fieldName] ?? [])]
      while (stack.length > 0) {
        const dep = stack.pop()!
        result.push(dep)
        stack.push(...(dependencyGraph[dep] ?? []))
      }
      return result
    },
    [dependencyGraph],
  )

  // Evidence uploads route through the same CLAIM_EVIDENCE upload endpoint the
  // shared FileUploader and evidence modal use. Returning the rich envelope
  // lets the metadata FileField render the filename/preview; single vs multiple
  // is driven purely by the field's ``configuration_data.file_multiple`` flag.
  const uploadFile = useUploadFile()

  // WPS-119: for a claim type that provisions its own registration, ask the
  // backend whether this property is already registered so the claim can be
  // attached to it instead of creating a second one at the same address.
  //
  // Debounced, because the address arrives keystroke-by-keystroke through the
  // lookup field and OC's rule needs all four values before it can match -
  // firing per character would be a request per letter for no better answer.
  const matchRegistration = useMatchingRegistration()
  // `null` means "not checked yet", which is NOT the same as the empty
  // signature: `matchSignature` is '' whenever the capture map is absent, so
  // seeding this with '' would make the guard below skip a genuine first
  // check. The catch path resets to null so the next keystroke retries.
  const matchCheckRef = useRef<string | null>(null)
  const registrationCapture = state.createsRegistration
    ? {
        first: state.capturedValues['property_owner_first_name'],
        last: state.capturedValues['property_owner_last_name'],
        phone: state.capturedValues['property_owner_phone'],
        address: state.capturedValues['property_address'],
      }
    : null
  // Serialise the inputs so the effect only re-runs when a value the match
  // actually depends on has changed.
  const matchSignature = registrationCapture
    ? JSON.stringify(registrationCapture)
    : ''

  useEffect(() => {
    if (!state.createsRegistration || !state.claimType) return
    const { first, last, phone, address } = registrationCapture ?? {}
    // Say nothing until every field the rule needs is present. A premature
    // "no match" is noise the RS has to mentally discard.
    const ready =
      Boolean(String(first ?? '').trim()) &&
      Boolean(String(last ?? '').trim()) &&
      Boolean(String(phone ?? '').trim()) &&
      Boolean(address)
    if (!ready) {
      if (state.matchedRegistration || state.matchCheckFailed) {
        update({
          matchedRegistration: null,
          attachToMatchedRegistration: false,
          matchCheckFailed: false,
        })
      }
      return
    }
    if (matchCheckRef.current === matchSignature) return
    const timer = setTimeout(() => {
      matchCheckRef.current = matchSignature
      matchRegistration
        .mutateAsync({
          claimSchemaKey: state.claimType as string,
          capturedValues: state.capturedValues,
        })
        .then((result) => {
          update({
            matchedRegistration: result,
            // Attach by default when it matched: all of OC's criteria agree,
            // so there is no decision left for the RS to make.
            attachToMatchedRegistration: Boolean(result?.matched),
            holdingUuid: result?.matched ? (result.holding_uuid ?? null) : null,
            matchCheckFailed: false,
          })
        })
        .catch(() => {
          // A failed lookup must never block the claim - it falls through to
          // the existing provision-on-submit behaviour. But it must not do so
          // silently: the RS is told on this step and again on review, because
          // a claim quietly landing on a second registration for a property
          // that is already on file is the failure this feature exists to
          // prevent. Clearing the ref lets the next keystroke retry rather
          // than leaving one transient error to suppress the check for good.
          matchCheckRef.current = null
          update({
            matchedRegistration: null,
            attachToMatchedRegistration: false,
            matchCheckFailed: true,
          })
        })
    }, 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchSignature, state.createsRegistration, state.claimType])

  const handleAttachChange = useCallback(
    (attach: boolean) => {
      const matched = state.matchedRegistration
      update({
        attachToMatchedRegistration: attach,
        // holdingUuid IS the switch the submit payload already sends: set it
        // to attach to the existing registration, clear it to provision a new
        // one. No change to the submit call needed.
        holdingUuid: attach ? (matched?.holding_uuid ?? null) : null,
      })
    },
    [state.matchedRegistration, update],
  )
  const handleFileUpload = useCallback(
    async (file: File) => {
      const uploaded = await uploadFile.mutateAsync({ file, use_case: 'CLAIM_EVIDENCE' })
      return {
        uuid: uploaded.uuid,
        file_name: uploaded.file_name,
        file_url: uploaded.file_url,
      }
    },
    [uploadFile],
  )

  // Seed capturedValues when fields load, from two sources: the field's static
  // configuration_data.default_value, and the server-supplied `prefill` map
  // (values copied from the registration, e.g. homeowner name/phone/email).
  // Prefill wins over a static default (it is the more specific value). Only
  // fields the user hasn't touched are filled, so edits are preserved.
  useEffect(() => {
    if (!fields.length) return
    const seeded: Record<string, unknown> = {}
    for (const f of fields) {
      if (state.capturedValues[f.code_name] !== undefined) continue
      const dv = f.configuration_data?.default_value
      if (dv !== undefined && dv !== null) seeded[f.code_name] = dv
    }
    for (const [code, value] of Object.entries(prefill)) {
      if (state.capturedValues[code] !== undefined) continue
      if (value !== undefined && value !== null && value !== '') seeded[code] = value
    }
    if (Object.keys(seeded).length > 0) {
      update({ capturedValues: { ...state.capturedValues, ...seeded } })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, prefill])

  // Validation errors keyed by field code_name. Populated when the user
  // clicks Next with required fields unfilled - the button stays enabled
  // and surfaces the missing fields inline rather than being disabled.
  const [errors, setErrors] = useState<Record<string, string>>({})

  if (isLoading) {
    return (
      <p className="py-10 text-center text-[13px] text-[var(--ink-3)]">Loading claim form...</p>
    );
  }

  if (fields.length === 0) {
    return (
      <div>
        <p className="py-5 text-[13px] text-[var(--ink-3)]">
          No additional details required for this claim type.
        </p>
        <div className="mt-6 flex justify-between">
          <Button onClick={back} variant="outline" className={ghostButtonClassName}>
            Back
          </Button>
          <Button onClick={next} variant="default" className={greenButtonClassName}>
            Next
          </Button>
        </div>
      </div>
    );
  }

  const handleChange = (code_name: string, value: unknown) => {
    const updated = { ...state.capturedValues, [code_name]: value }
    for (const dep of collectDependents(code_name)) {
      updated[dep] = null
    }
    update({ capturedValues: updated })
    // Clear the error as soon as the user starts filling the field.
    setErrors((prev) => {
      if (!prev[code_name]) return prev
      const updated = { ...prev }
      delete updated[code_name]
      return updated
    })
  }

  // Pre-fill sibling fields when an ENTITY_SEARCH field with a prefill_map is
  // selected (e.g. picking a retailer fills the retailer contact rows). The
  // reducer merges capturedValues, so this dispatch layers on top of the
  // picker's own value set moments earlier; the filled fields stay editable.
  const handlePrefill = (values: Record<string, unknown>) => {
    update({ capturedValues: { ...state.capturedValues, ...values } })
  }

  const visibleFields = fields.filter((f) => {
    // Hide claim_type - already selected in step 1
    if (f.code_name === 'claim_type') return false
    // Hide deprecated attributes left over from previous seed runs
    if (f.configuration_data?.deprecated) return false
    return isFieldVisible(f.configuration_data, state.capturedValues)
  })

  // Validate required fields on click. The Next button stays enabled; if any
  // required field is empty we surface inline errors and hold the step rather
  // than greying out the button.
  const handleNext = () => {
    const nextErrors: Record<string, string> = {}
    for (const f of visibleFields) {
      const effectiveRequired =
        f.required || isConditionallyRequired(f.configuration_data, state.capturedValues)
      if (!effectiveRequired) continue
      // File/evidence fields are validated like any other required field: the
      // requiredness is metadata-driven (the CREATION capture config), so a
      // tenant that wants evidence optional at admin intake sets required=false
      // there. Skipping them here is what let the page disagree with the
      // backend serializer (which enforces the schema-level required) and
      // produced a "field is required" error the form never surfaced.
      // `isRequiredValueProvided` centralises the emptiness test: multi-select
      // `[]`, MONEY/CURRENCY `{currency}` with no `amount`, and - critically -
      // a required acknowledgement checkbox that is `false` (check-then-uncheck)
      // all count as "not provided" (DL-44/CLM-017).
      const v = state.capturedValues[f.code_name]
      if (!isRequiredValueProvided(f, v)) {
        nextErrors[f.code_name] = `${f.display_name} is required`
      }
    }
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length === 0) next()
  }

  const nonFileFields = visibleFields.filter((f) => !FILE_FIELD_TYPES.has((f.field_type || '').toUpperCase()))
  const fileFields = visibleFields.filter((f) => FILE_FIELD_TYPES.has((f.field_type || '').toUpperCase()))

  // For Uninstalled / Job Stop claims, use explicit column assignment:
  // Left = supplier/contractor info, Right = product & defect details.
  // For other types, fall back to a simple midpoint split.
  const RIGHT_COLUMN_FIELDS = new Set([
    // The product and its colour sit together (WPS-164 #2 / WPS-178).
    'product_impacted', 'product_name', 'color', 'quantity', 'component_type', 'lumber_type',
    'run_numbers', 'major_category', 'minor_category',
    'description', 'date_of_incident',
    // `material_at_job_site` + its dependent `material_location` intentionally
    // stay in the left "Claim Details" card so the toggle sits with the field
    // it reveals (WP1-106).
    // Job stop specific
    'amount_ordered', 'amount_impacted', 'percent_job_complete',
    'amount_outstanding', 'additional_labor_hours_needed', 'additional_labor_hours',
  ])
  // Supplier/contractor claim types get the explicit two-column layout. Keyed on
  // the supplier section generally (not `supplier_name`) so removing the Supplier
  // Name field (WP1-92) doesn't collapse the layout back to the naive split.
  const hasSupplierFields = nonFileFields.some((f) => f.code_name.startsWith('supplier_'))
  const hasExplicitLayout =
    hasSupplierFields || nonFileFields.some((f) => f.code_name === 'contractor_name')
  // A field marked ``rendered_inline_by`` is drawn by its composite sibling
  // (e.g. the unit half of a quantity+UOM composite renders inside the
  // quantity field), never on its own - drop it from both columns.
  const isInlineRendered = (f: (typeof nonFileFields)[number]) =>
    Boolean(
      (f.configuration_data as Record<string, unknown> | undefined)
        ?.rendered_inline_by,
    )
  const columnFields = nonFileFields.filter((f) => !isInlineRendered(f))
  const leftFields = hasExplicitLayout
    ? columnFields.filter((f) => !RIGHT_COLUMN_FIELDS.has(f.code_name))
    : columnFields.slice(0, Math.ceil(columnFields.length / 2))
  const rightFields = hasExplicitLayout
    ? columnFields.filter((f) => RIGHT_COLUMN_FIELDS.has(f.code_name))
    : columnFields.slice(Math.ceil(columnFields.length / 2))

  // Build the composite unit-of-measurement companion for a field that
  // declares ``configuration_data.uom_field`` (opt-in, config-driven; no
  // tenant branch). The companion's unit options cascade off live values
  // (e.g. product type), exactly like a standalone dependent SELECT.
  const buildUomCompanion = (field: (typeof nonFileFields)[number]) => {
    const cfg = field.configuration_data as Record<string, unknown> | undefined
    const uomFieldName = cfg?.uom_field as string | undefined
    if (!uomFieldName) return undefined
    const uomCfg = cfg?.uom as Record<string, unknown> | undefined
    return {
      options: resolveDependentOptions(uomCfg, state.capturedValues) ?? [],
      value: state.capturedValues[uomFieldName],
      onChange: (v: unknown) => handleChange(uomFieldName, v),
      error: errors[uomFieldName],
      placeholder: (uomCfg?.placeholder as string) || 'Unit',
    }
  }

  return (
    <div>
      {/* Two-card grid matching registration wizard style */}
      <div data-tour="claim-details-form" className="grid grid-cols-1 sm:grid-cols-2 items-stretch gap-6">
        {/* Left card - Claim Details */}
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
          <div className="mb-4 flex items-center gap-2">
            <p className="m-0 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
              Claim Details
            </p>
          </div>
          {state.holdingDisplay && (
            <p
              className="mb-4 truncate text-[12px] text-[var(--ink-3)]"
              title={state.holdingDisplay}
            >
              {state.holdingDisplay}
            </p>
          )}
          {/* Supplier lookup coming-soon banner - shown when the
              form includes supplier fields (Uninstalled claims). */}
          {hasSupplierFields && (
            <div className="mb-4 flex items-center gap-2.5 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3.5 py-2.5">
              <Search size={14} className="shrink-0 text-[var(--ink-3)]" />
              <p className="m-0 text-[11px] leading-[1.4] text-[var(--ink-3)]">
                <span className="font-medium text-[var(--ink-2)]">Supplier lookup coming soon</span>
                {' - '}SAP integration will auto-fill supplier details. For now, enter manually.
              </p>
            </div>
          )}
          <div className="flex flex-col gap-4">
            {leftFields.map((field) => {
              const normalised = normaliseClaimField(field)
              const depOptions = resolveDependentOptions(field.configuration_data, state.capturedValues)
              if (depOptions) normalised.options = depOptions
              if (!normalised.required && isConditionallyRequired(field.configuration_data, state.capturedValues)) {
                normalised.required = true
              }
              return (
                <MetadataField
                  key={field.code_name}
                  field={normalised}
                  value={state.capturedValues[field.code_name]}
                  onChange={(v) => handleChange(field.code_name, v)}
                  onPrefill={handlePrefill}
                  error={errors[field.code_name]}
                  labelVariant="compact"
                  companion={buildUomCompanion(field)}
                  siblingValues={state.capturedValues}
                />
              )
            })}
          </div>
        </div>

        {/* Right card - Additional Info */}
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
          <div className="mb-4 flex items-center gap-2">
            <p className="m-0 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
              {hasExplicitLayout ? 'Product & Defect Details' : 'Additional Information'}
            </p>
          </div>
          <div className="flex flex-col gap-4">
            {rightFields.map((field) => {
              // Product Name renders from its schema config (the catalogue
              // picker it declares), like every other field.
              const normalised = normaliseClaimField(field)
              const depOptions = resolveDependentOptions(field.configuration_data, state.capturedValues)
              if (depOptions) normalised.options = depOptions
              if (!normalised.required && isConditionallyRequired(field.configuration_data, state.capturedValues)) {
                normalised.required = true
              }
              return (
                <MetadataField
                  key={field.code_name}
                  field={normalised}
                  value={state.capturedValues[field.code_name]}
                  onChange={(v) => handleChange(field.code_name, v)}
                  onPrefill={handlePrefill}
                  error={errors[field.code_name]}
                  labelVariant="compact"
                  companion={buildUomCompanion(field)}
                  siblingValues={state.capturedValues}
                />
              )
            })}
            {/* File / evidence uploads render through the shared metadata
                FileField, which wraps the same multi-file FileUploader used by
                the evidence modal. Single vs multiple is driven entirely by the
                field's ``configuration_data.file_multiple`` flag (normalised to
                ``file_multiple`` by normaliseClaimField) - a frontend hint the
                backend ignores, since it normalises bare UUIDs, envelopes, and
                arrays alike. Requiredness is metadata-driven, exactly like every
                other field: a required evidence field shows a marker and is
                validated on Next, matching the backend serializer. A tenant that
                wants evidence optional at admin intake sets required=false in the
                capture config. */}
            {fileFields.map((field) => {
              const base = normaliseClaimField(field)
              // Override `required` immutably (never mutate the normalised
              // object in render) when the field is conditionally required.
              const normalised =
                !base.required &&
                isConditionallyRequired(field.configuration_data, state.capturedValues)
                  ? { ...base, required: true }
                  : base
              return (
                <MetadataField
                  key={field.code_name}
                  field={normalised}
                  value={state.capturedValues[field.code_name]}
                  onChange={(v) => handleChange(field.code_name, v)}
                  error={errors[field.code_name]}
                  onFileUpload={handleFileUpload}
                  multiple={!!normalised.file_multiple}
                  labelVariant="compact"
                />
              )
            })}
          </div>
        </div>
      </div>
      {state.createsRegistration ? (
        <RegistrationMatchPanel
          match={state.matchedRegistration}
          isChecking={matchRegistration.isPending}
          checkFailed={state.matchCheckFailed}
          attach={state.attachToMatchedRegistration}
          onAttachChange={handleAttachChange}
        />
      ) : null}
      <div className="mt-6 flex justify-between">
        <Button onClick={back} variant="outline" className={ghostButtonClassName}>
          Back
        </Button>
        <Button
          onClick={handleNext}
          variant="default"
          className={greenButtonClassName}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

// ── Step 3: Review & Submit ──────────────────────────────

function StepReview({
  state,
  back,
  fields,
  onSubmit,
  isSubmitting,
  disclaimer,
  acknowledged,
  onAcknowledgedChange,
}: {
  state: WizardState
  back: () => void
  fields: ActionFieldMeta[]
  onSubmit: () => void
  isSubmitting: boolean
  disclaimer: import('@/shared/components/claims/disclaimer').DisclaimerConfig | null
  acknowledged: boolean
  onAcknowledgedChange: (acknowledged: boolean) => void
}) {
  const { t } = useTranslation()
  const { locale, currency } = useFormatting()
  const { data: claimTypeData } = useClaimTypeOptions()
  const claimTypeLabel = useMemo(() => {
    if (!state.claimType) return null
    const option = claimTypeData?.options.find((o) => o.value === state.claimType)
    return option?.label ?? state.claimType
  }, [claimTypeData, state.claimType])
  // Same reference the registration page and emails show, so it is never blank.
  const registrationReference = formatRegistrationReference({
    registration_number: state.holdingRegistrationNumber,
    uuid: state.holdingUuid,
  })

  return (
    <div className="max-w-3xl">
      <h2 className="mb-5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
        Review & Submit
      </h2>
      {/* Claim Type */}
      {state.claimType && (
        <div className="mb-4 rounded-[8px] bg-[var(--bg-subtle)] px-4 py-[14px]">
          <p className="mb-1.5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Claim Type
          </p>
          <p className="text-[14px] text-[var(--ink)]">
            {claimTypeLabel}
          </p>
        </div>
      )}
      {/* WPS-119: for a claim type that provisions its own registration, say
          which way it will go BEFORE submit. The RS made this choice one step
          back, on a form they have since left - repeating it here is what stops
          a claim landing on the wrong property without anyone noticing. */}
      {state.createsRegistration && (
        <div className="mb-4 rounded-[8px] bg-[var(--bg-subtle)] px-4 py-[14px]">
          <p className="mb-1.5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {t('submitClaim.review.registration', 'Registration')}
          </p>
          <p className="text-[14px] text-[var(--ink)]">
            {state.attachToMatchedRegistration && state.matchedRegistration?.matched
              ? state.matchedRegistration.registration_number
                ? t('submitClaim.review.addingToNumber', 'Adding to {{number}}', {
                    number: state.matchedRegistration.registration_number,
                  })
                : t(
                    'submitClaim.review.addingToExisting',
                    'Adding to the existing registration',
                  )
              : t(
                  'submitClaim.review.newWillBeCreated',
                  'A new registration will be created',
                )}
          </p>
          {!state.attachToMatchedRegistration && state.matchCheckFailed ? (
            <p className="mt-1 text-[12px] font-light text-[var(--ink-3)]">
              {t(
                'submitClaim.review.checkFailed',
                'We could not check whether this property is already registered.',
              )}
            </p>
          ) : null}
        </div>
      )}
      {/* Registration - hidden for holdingless claim types */}
      {state.holdingDisplay && (
        <div className="mb-4 rounded-[8px] bg-[var(--bg-subtle)] px-4 py-[14px]">
          <p className="mb-1.5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Registration
          </p>
          {registrationReference && (
            <p className="font-mono text-[14px] text-[var(--ink)]">
              {registrationReference}
            </p>
          )}
          <p
            className={`text-[13px] ${
              registrationReference
                ? 'text-[var(--ink-3)]'
                : 'text-[var(--ink)]'
            }`}
          >
            {state.holdingDisplay}
          </p>
        </div>
      )}
      {/* Claim fields */}
      {fields.length > 0 && (
        <div className="mb-4 rounded-[8px] bg-[var(--bg-subtle)] px-4 py-[14px]">
          <p className="mb-2.5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Claim Details
          </p>
          {fields
            .filter((f) => isFieldVisible(f.configuration_data, state.capturedValues))
            .map((field) => {
              const value = state.capturedValues[field.code_name]
              const normalised = normaliseClaimField(field)
              const depOptions = resolveDependentOptions(field.configuration_data, state.capturedValues)
              if (depOptions) normalised.options = depOptions
              return (
                <div
                  key={field.code_name}
                  className="flex items-baseline justify-between border-b-[0.5px] border-[var(--rule)] py-1.5"
                >
                  <span className="text-[12px] text-[var(--ink-3)]">
                    {field.display_name}
                  </span>
                  <span className="text-[13px] text-[var(--ink)]">
                    {formatReviewValue(normalised, value, { locale, currency })}
                  </span>
                </div>
              );
            })}
        </div>
      )}
      {disclaimer && (
        <DisclaimerAck
          className="mt-4"
          disclaimer={disclaimer}
          acknowledged={acknowledged}
          onAcknowledgedChange={onAcknowledgedChange}
        />
      )}
      <div className="mt-6 flex justify-between">
        <Button onClick={back} disabled={isSubmitting} variant="outline" className={ghostButtonClassName}>
          Back
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isSubmitting || disclaimerBlocks(disclaimer, acknowledged)}
          variant="default"
          className={`${greenButtonClassName} ${isSubmitting ? 'opacity-70' : ''}`}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Claim'}
        </Button>
      </div>
    </div>
  );
}

const greenButtonClassName =
  'cursor-pointer rounded-[6px] bg-[var(--green)] px-6 py-2.5 text-[12px] font-normal uppercase tracking-[0.08em] text-white'

const ghostButtonClassName =
  'cursor-pointer rounded-[6px] border border-input bg-transparent px-6 py-2.5 text-[12px] font-normal uppercase tracking-[0.08em] text-[var(--ink-2)]'
